import React, { useEffect, useState} from "react"
import Header from "../../components/navigation/Header"
import GetWindowSize from '../../hooks/general/GetWindowSize'
import CurrentGoal from "../../components/general/athlete-preview/CurrentGoal"
import SocialMediaIcons from "../../components/general/coach-preview/SocialMediaIcons"
import { faArrowLeftLong, faChevronRight, faGraduationCap, faLocationDot, faStar } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import axios from "axios"
import GetCSFR from "../../hooks/userAuthentication/GetCSFR"
import EditAthleteProfileForm from "../../components/general/athlete-preview/EditAthleteProfileForm"
import EditCoachProfileForm from "../../components/general/coach-preview/EditCoachProfileForm"
import EditCoachServiceForm from "../../components/general/coach-preview/EditCoachServiceForm"
import ReviewSlider from "../../components/general/coach-preview/ReviewSlider"
import CoachService from "../../components/general/coach-preview/CoachService"
import Footer from "../../components/navigation/Footer"
import RetrieveImage from "../../hooks/images/RetrieveImage"
import { UserContext } from "../../hooks/images/RetrieveImageContext"
import LoadingAnimation from "../../components/general/LoadingAnimation"
import { useNavigate } from "react-router-dom"

interface Review {
    id: number;
    title: string;
    description: string;
    rating: string;
    date: string;
}

interface Service {
    id?: number;
    type: string;
    title: string;
    description: string;
    duration: string;
    frequency?: string;
    target_audience?: string;
    session_length?: number;
    location?: string;
    deliverable?: string;
    price: number;
}
  
interface ProfileDetails {
    fullname: string;
    email: string;
    iscoach: boolean;
    isathlete: boolean;
    phonenumber: string;
    profile: {
        location: string;
        biography: string;
        primary_sport: string;
        picture: string | null;
        reviews: Review[];
        services?: Service[];
        rating: number;
        instagram?: string,
        facebook?: string,
        twitter?: string,
        tiktok?: string
    };
    preferences: {
        experience_level: string;
        goals?: string[];
        sport_interests?: string[];
        age_groups?: string[];
        specialization?: string[];
    }
}

// Default values for Service
const defaultService: Service = {
    type: '',
    title: '',
    description: '',
    duration: '',
    frequency: '',
    target_audience: '',
    location: '',
    deliverable: '',
    session_length: 0,
    price: 0
}

// Default values for profileDetails
const defaultProfileDetails: ProfileDetails = {
    fullname: 'Name',
    email: 'Email',
    iscoach: false,
    isathlete: false,
    phonenumber: 'Phone Number #',
    profile: {
        location: 'Location',
        biography: 'Bio goes here...',
        primary_sport:'',
        picture: '',
        reviews: [],
        services: [],
        rating: 0,
        instagram: '',
        facebook: '',
        twitter: '',
        tiktok: ''
    },
    preferences: {
        experience_level: 'N/A',
        goals: [],
        sport_interests: [],
        age_groups: [],
        specialization: []
    },
}

export default function Profile(){
    const [selectedService, setSelectedService] = useState<Service>(defaultService)
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
    const [profileDetails, setProfileDetails] = useState<ProfileDetails>(defaultProfileDetails)
    const windowSize = GetWindowSize()
    const csrfToken = GetCSFR({ name: "csrftoken" })
    const [readyToDisplayProfileForm, setReadyToDisplayProfileForm] = useState<boolean>(false)
    const [readyToDisplayServicesForm, setReadyToDisplayServicesForm] = useState<boolean>(false)
    const [savedMediaFiles, setSavedMediaFiles] = useState<string[]>(["","","","","",""])
    const [loading, setLoading] = useState<boolean>(true)
    const navigate = useNavigate()

    // API call to get user profile details
    useEffect(()=>{
        document.title = "SkillJa - Profile"
        axios.get(`${process.env.REACT_APP_SKILLJA_URL}/auth/profile/`, { 
            headers: {
                'X-CSRFToken': csrfToken,
                'Content-Type': 'application/json'
            },
            withCredentials: true
        }) 
            .then(res => {
                if (res.status === 200) {
                    setProfileDetails(res.data.user_profile)
                } else {
                    console.error("Failed to retrieve profile details")
                }
            })
            .catch(error => {console.error(error)})
        
    },[])

    // Force simulate loading animation while Coach data is being fetched
    useEffect(()=>{
        const timer = setTimeout(() => {
            setLoading(false)
        }, 1500)
        return ()=> clearTimeout(timer)
    },[])
    

    return (
        <div className="flex flex-col">
            {profileDetails.profile.picture ? 
                <UserContext.Provider value={{imageName:profileDetails.profile.picture, cache:true}}>
                    <Header useCase="protected" /> 
                </UserContext.Provider> 
            :
                <UserContext.Provider value={{imageName:"", cache:false}}>
                    <Header useCase="protected" /> 
                </UserContext.Provider> 
            }
            {/* force simulate loading simulation while API data loads */}
            <div className={`${loading ? 'absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2': 'opacity-0 -top-10'}`}>
                <LoadingAnimation />
            </div> 
            <div className={`pb-4 px-8 lg:px-32 xl:px-40 lg:mb-32 ${loading ? 'opacity-0':'opacity-100'}`}> 
                <section className="grid grid-cols-3 items-center">
                    <FontAwesomeIcon 
                        icon={faArrowLeftLong} 
                        className="text-3xl hover:text-main-green-500 cursor-pointer justify-self-start"
                        onClick={()=> navigate('/auth/settings')}
                    />
                    {profileDetails.isathlete && readyToDisplayProfileForm && <EditAthleteProfileForm displayForm={setReadyToDisplayProfileForm} prevSavedData={profileDetails} savedMediaFiles={savedMediaFiles} setSavedMediaFiles={setSavedMediaFiles}/>}
                    {profileDetails.iscoach && readyToDisplayProfileForm && <EditCoachProfileForm displayForm={setReadyToDisplayProfileForm} prevSavedData={profileDetails} savedMediaFiles={savedMediaFiles} setSavedMediaFiles={setSavedMediaFiles} />}
                    {profileDetails.iscoach && readyToDisplayServicesForm && <EditCoachServiceForm displayForm={setReadyToDisplayServicesForm}/>}
                    <h1 className="font-kulim text-2xl sm:text-2xl lg:text-3xl text-center text-main-green-900 font-semibold">
                        {profileDetails.fullname? profileDetails.fullname : 'Profile' }
                    </h1>
                    {/* <div className="justify-self-end w-8"></div> */}
                    <div className={`flex ml-auto flex-col md:flex-row`}>
                        <button 
                            className="py-1.5 px-2 md:px-3.5 mb-2 md:mb-0 text-xs md:text-sm bg-main-green-500 text-main-white font-kulim rounded-xl hover:bg-main-green-700"
                            onClick={()=>setReadyToDisplayProfileForm(true)}
                        >
                            Edit Profile
                        </button>
                        {profileDetails.iscoach &&
                            <div className="flex flex-col md:ml-3 text-xs md:text-sm justify-center items-center">
                                <button 
                                    className="py-1.5 px-2 md:px-3.5 bg-main-green-500 text-main-white font-kulim rounded-xl hover:bg-main-green-700"
                                    onClick={()=>setReadyToDisplayServicesForm(true)}
                                >
                                    Edit Services
                                </button>
                            </div>
                        }
                    </div>
                </section>
                
                <section className="flex flex-col border-b-4 mt-10 lg:mt-14 border-main-grey-200 pb-4 sm:pb-8">
                    <div className={`flex ${windowSize.width < 1200 && 'flex-wrap'} items-start`}>
                        <div className={`flex flex-wrap shrink md:shrink-0 max-w-full ${windowSize.width < 1200 && 'mx-auto'} `}>
                            {profileDetails.profile.picture && (
                                <UserContext.Provider value={{imageName:profileDetails.profile.picture!, cache:true}}>
                                    <RetrieveImage position="one" styling="w-40 h-40 md:w-60 md:h-60 rounded-2xl mr-3 xl:mr-6" storeMedia={setSavedMediaFiles}/>
                                </UserContext.Provider> 
                            )}
                            {!profileDetails.profile.picture && (
                                <img src={require('../../assets/default-avatar.jpg')} className="w-32 h-32 lg:w-44 lg:h-44 rounded-2xl lg:mr-3 xl:mr-6" />
                            )}
                            <div className={`grid ${windowSize.width < 420 ? 'grid-cols-1 grid-rows-4 gap-1' : 'grid-cols-2 grid-rows-2 gap-4'} min-w-fit ${windowSize.width > 1190 && 'mr-4'} xl:mr-6`}>
                                {profileDetails.profile.picture ? (
                                    <UserContext.Provider value={{imageName:`${profileDetails.profile.picture!}two`, cache:false}}>
                                        <RetrieveImage id={`${profileDetails.profile.picture!}two`} position='two' styling={`${windowSize.width < 420 ? 'w-9 h-9' : 'w-16 h-16'} md:min-w-28 md:min-h-28 rounded-lg sm:rounded-2xl border object-cover`} storeMedia={setSavedMediaFiles} />
                                    </UserContext.Provider>
                                )
                                :
                                    <img src={require('../../assets/default-avatar.jpg')} className={`${windowSize.width < 420 ? 'w-9 h-9' : 'w-16 h-16'} md:min-w-28 md:min-h-28 rounded-lg sm:rounded-2xl border`}/>
                                }
                                {profileDetails.profile.picture ? (
                                    <UserContext.Provider value={{imageName:`${profileDetails.profile.picture!}three`, cache:false}}>
                                        <RetrieveImage id={`${profileDetails.profile.picture!}three`} position='three' styling={`${windowSize.width < 420 ? 'w-9 h-9' : 'w-16 h-16'} md:min-w-28 md:min-h-28 rounded-lg sm:rounded-2xl border object-cover`} storeMedia={setSavedMediaFiles} />
                                    </UserContext.Provider>
                                )
                                :
                                    <img src={require('../../assets/default-avatar.jpg')} className={`${windowSize.width < 420 ? 'w-9 h-9' : 'w-16 h-16'} md:min-w-28 md:min-h-28 rounded-lg sm:rounded-2xl border`}/>
                                }
                                {profileDetails.profile.picture ? (
                                    <UserContext.Provider value={{imageName:`${profileDetails.profile.picture!}four`, cache:false}}>
                                        <RetrieveImage id={`${profileDetails.profile.picture!}four`} position='four' styling={`${windowSize.width < 420 ? 'w-9 h-9' : 'w-16 h-16'} md:min-w-28 md:min-h-28 rounded-lg sm:rounded-2xl border object-cover`} storeMedia={setSavedMediaFiles}/>
                                    </UserContext.Provider>
                                )
                                :
                                    <img src={require('../../assets/default-avatar.jpg')} className={`${windowSize.width < 420 ? 'w-9 h-9' : 'w-16 h-16'} md:min-w-28 md:min-h-28 rounded-lg sm:rounded-2xl border`}/>
                                }
                                {profileDetails.profile.picture ? (
                                    <UserContext.Provider value={{imageName:`${profileDetails.profile.picture!}video`, cache:false}}>
                                        <RetrieveImage id={`${profileDetails.profile.picture!}video`} position='video' styling={`${windowSize.width < 420 ? 'w-9 h-9' : 'w-16 h-16'} md:min-w-28 md:min-h-28 rounded-lg sm:rounded-2xl border`} storeMedia={setSavedMediaFiles}/>
                                    </UserContext.Provider>
                                )
                                :
                                    <img src={require('../../assets/default-video-avatar.png')} className={`${windowSize.width < 420 ? 'w-9 h-9' : 'w-16 h-16'} md:min-w-28 md:min-h-28 rounded-lg sm:rounded-2xl border`}/>
                                }
                            </div>
                        </div>
                        <div className={`flex flex-col justify-start items-start w-full break-words max-w-lg xl:max-w-xl ${windowSize.width > 1200 ? 'ml-auto' :'mx-auto mt-9 mb-5'} min-w-0 mb-auto`}>
                            <div className="flex w-full items-start">
                                <div className="flex flex-col justify-center items-start font-kulim text-main-green-900 md:pr-16 pt-1">
                                    <h3 className="text-sm sm:text-base mb-1 mx-auto font-medium font-kulim ml-0">
                                        <FontAwesomeIcon icon={faLocationDot} className="text-base md:text-xl w-6 mr-1.5 md:mr-3.5" />
                                        {profileDetails.profile.location? profileDetails.profile.location : 'Location' }
                                    </h3>
                                    <h3 className="text-sm sm:text-base my-5 sm:my-3 xl:my-4 mx-auto font-medium font-kulim ml-0">
                                        <FontAwesomeIcon icon={faGraduationCap} className="text-base md:text-xl w-6 mr-1.5 md:mr-3.5" />
                                        {profileDetails.preferences.experience_level ? `${profileDetails.preferences.experience_level.replace(/[\[\]\"\'']/g,'')} coaching `: 'N/A' }
                                    </h3>
                                    <h3 className="text-sm sm:text-base mt-1 mx-auto font-medium ml-0">
                                        <FontAwesomeIcon icon={faStar} className="text-amber-500 w-6 text-base md:text-xl mr-1.5 md:mr-3.5" />
                                        {Number(profileDetails.profile?.rating).toPrecision(2).toString().includes('0.0') || profileDetails.profile?.rating.toString() === '0' ? 'No Ratings' : Number(profileDetails.profile?.rating).toPrecision(2)} 
                                    </h3>
                                </div>
                                <div className='flex flex-col justify-evenly items-end ml-auto text-sm'>
                                    {profileDetails.profile.picture && 
                                        <UserContext.Provider value={{imageName:`${profileDetails.profile.picture!}club`, cache:false}}>
                                            <RetrieveImage id={`${profileDetails.profile.picture!}club`} position='club' styling="object-fit rounded-xl w-24 h-9 mb-2" storeMedia={setSavedMediaFiles}/>
                                        </UserContext.Provider>
                                    }
                                    <h3 className="py-1.5 px-3 text-sm rounded-xl mb-2.5 bg-main-white border border-main-grey-100">
                                        {profileDetails.profile?.primary_sport || 'N/A'}
                                    </h3>
                                    <SocialMediaIcons 
                                        instagram={profileDetails.profile.instagram!}
                                        facebook={profileDetails.profile.facebook!}
                                        twitter={profileDetails.profile.twitter!}
                                        tiktok={profileDetails.profile.tiktok!}
                                    />
                                </div>
                            </div>
                            <div className="w-full mt-7 sm:mt-4 md:mt-3 lg:mt-5 mb-auto text-wrap pl-1">
                                <h3 className="text-sm sm:text-base my-1 mr-auto font-semibold font-kulim">About Me:</h3>
                                <p className="mt-1.5 min-h-16 text-sm sm:text-base text-start font-kulim leading-tight">
                                    {profileDetails.profile.biography? profileDetails.profile.biography : 'Bio goes here...' }
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="flex justify-center items-center flex-col lg:flex-row lg:items-start my-8 mx-auto lg:pt-4">
                    <div className="flex flex-col justify-center w-full font-kulim lg:w-5/12 lg:mr-auto">
                    {profileDetails.isathlete ?
                        <>
                            <h2 className="text-lg sm:text-xl font-medium mx-auto mb-4 text-center">
                                Current Goals
                            </h2>
                            {profileDetails.preferences.goals!.length > 0 ?
                                <>
                                    {profileDetails.preferences.goals!.map((currGoal, index) => (
                                        <CurrentGoal key={index} goal={currGoal} />
                                    ))}
                                </>
                            :
                                <p className="font-kulim">No goals to display</p>
                            }
                        </>
                    :
                        <>
                            <h2 className="text-lg sm:text-xl font-medium mx-auto mb-6 text-center">
                                Sessions and Packages
                            </h2>
                            {(profileDetails.profile.services || []).length > 0 ?
                                <>
                                    {(profileDetails.profile.services || []).map((currService, index) => (
                                    <div
                                        className="flex rounded-2xl py-2 px-5 mb-4 bg-main-white border border-main-grey-100 cursor-pointer hover:border-main-green-500 hover:shadow-md"
                                        onClick={()=>{
                                            setIsModalOpen(true)
                                            setSelectedService(currService)
                                        }}
                                        key={index}
                                    >
                                        <div className="flex flex-col justify-start items-start font-kulim">
                                            <h3 className="my-1 mr-auto">
                                                {currService.title}
                                            </h3>
                                            <p className="text-sm my-1 mr-auto">
                                                See Price and Details
                                            </p>
                                        </div>
                                        <FontAwesomeIcon icon={faChevronRight} className="text-main-grey-300 ml-auto my-auto" />
                                    </div>
                                    ))}
                                </>
                            :
                                <p className="mx-auto font-kulim">No services are currently available</p>
                            }
                        </>
                    }
                    </div>
                    <div className="flex flex-col justify-center mt-12 font-kulim lg:mt-0 w-full lg:w-5/12 lg:ml-auto">
                        <h2 className="text-lg sm:text-xl font-medium mx-auto mb-6 text-center">
                            Reviews and Testimonials
                        </h2>
                        {profileDetails.profile.reviews?.length > 0 ?
                            <ReviewSlider data={profileDetails.profile.reviews}/>   
                        :
                            <p className="mx-auto mt-4 font-kulim">No reviews available</p>    
                        }
                    </div>
                </section>
            </div>
            {isModalOpen && <CoachService data={selectedService} exitView={setIsModalOpen}/> }
            
            <Footer />
        </div>
    )
}